import React from 'react';
import { Link } from 'react-router-dom';
import { SkewButton, Card, GradientDivider } from '../components/UIComponents';
import { Zap, ShoppingBag, Calendar, Info, ArrowRight, Flame, Dumbbell } from 'lucide-react';

const WATTS_LOGO = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/i88xwms2_Blue%20Playful%20Closed%20Due%20To%20Weather%20Facebook%20Post.png";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1623804421482-721b3901c125?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxuZW9uJTIwZ3ltJTIwbGlnaHRpbmclMjBlbmVyZ2V0aWMlMjBmaXRuZXNzJTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NzA3NjM2MTJ8MA&ixlib=rb-4.1.0&q=85",
};

const actionTiles = [
  {
    icon: ShoppingBag,
    title: "Shop Merch",
    description: "Rep the power. Official 2000WATTS gear for those who put in the work.",
    cta: "Shop Now",
    link: "https://viagro.com",
  },
  {
    icon: Calendar,
    title: "Book Sessions / View Classes",
    description: "High-intensity sessions that push limits. No excuses. Just results.",
    cta: "Book Now",
    link: "https://viagro.com",
  },
  {
    icon: Info,
    title: "About 2000WATTS",
    description: "The energy behind the movement. Learn what powers us.",
    cta: "Learn More",
    link: "#about-2000watts",
  }
];

const merchandise = [
  { name: "2000WATTS Hoodies", price: "$70", cta: "Shop Hoodies" },
  { name: "2000WATTS T-Shirts", price: "$35", cta: "Shop Shirts" },
  { name: "2000WATTS Racerback Tanks", price: "$32", cta: "Shop Racerbacks" },
  { name: "2000WATTS Hats", price: "$30", cta: "Shop Hats" },
];

export const ShockTheBodyPage = () => {
  return (
    <div data-testid="shockthebody-page" className="bg-brand-black min-h-screen">
      {/* Custom 2000WATTS Header */}
      <header data-testid="watts-header" className="fixed top-0 w-full z-50 bg-brand-black/95 backdrop-blur-md border-b border-brand-lime/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-28">
            <Link to="/shock-the-body" data-testid="watts-logo">
              <img 
                src={WATTS_LOGO} 
                alt="2000WATTS - Feel The Power" 
                className="h-24 w-auto"
              />
            </Link>
          </div>
        </div>
        {/* Navigation back to main site */}
        <div className="bg-brand-lime/10 py-2">
          <div className="max-w-7xl mx-auto px-4 flex justify-center gap-6">
            <Link to="/" className="text-xs font-oswald uppercase tracking-widest text-brand-gray hover:text-brand-lime transition-colors">
              ← Back to Power Up Your Fitness
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section data-testid="watts-hero" className="relative pt-44 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="2000WATTS" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-brand-black/90 to-brand-black" />
          {/* Electric effect overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-brand-lime/10 via-transparent to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          {/* Lightning bolt accent */}
          <div className="flex justify-center mb-6">
            <Zap size={60} className="text-yellow-400 animate-pulse" />
          </div>
          
          <h1 className="font-oswald text-6xl md:text-8xl lg:text-9xl font-bold uppercase tracking-tighter text-white mb-4">
            SHOCK THE<br />
            <span className="text-brand-lime">BODY</span>
          </h1>
          
          <p className="font-oswald text-xl md:text-2xl uppercase tracking-[0.2em] text-brand-lime mb-6">
            Powered by 2000WATTS.
          </p>
          
          <div className="w-48 h-1 bg-gradient-to-r from-transparent via-brand-lime to-transparent mx-auto mb-8" />
          
          <p className="font-oswald text-2xl md:text-3xl uppercase tracking-wider text-white mb-8">
            Energy. Intensity. Results.
          </p>
          
          <p className="font-montserrat text-lg md:text-xl text-brand-gray leading-relaxed mb-12 max-w-3xl mx-auto">
            This is where the work happens. High-energy training that pushes limits, builds strength, and transforms confidence. <span className="text-white font-semibold">If you came to play, this is not your room.</span> If you came to work, welcome.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <SkewButton href="https://viagro.com" testId="watts-shop-cta" className="text-lg px-10 py-4">
              Shop 2000WATTS Merch
            </SkewButton>
            <SkewButton variant="outline" href="https://viagro.com" testId="watts-book-cta" className="text-lg px-10 py-4">
              Book 2000WATTS Sessions
            </SkewButton>
          </div>
        </div>
      </section>

      {/* Action Tiles Section */}
      <section data-testid="watts-action-tiles" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {actionTiles.map((tile, index) => (
              <a
                key={tile.title}
                href={tile.link}
                target={tile.link.startsWith('http') ? "_blank" : undefined}
                rel={tile.link.startsWith('http') ? "noopener noreferrer" : undefined}
                data-testid={`watts-tile-${index}`}
                className="group"
              >
                <Card className="p-8 h-full text-center border-2 border-brand-lime/20 hover:border-brand-lime transition-all duration-300 bg-brand-black">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-lime/10 flex items-center justify-center group-hover:bg-brand-lime/30 transition-colors">
                    <tile.icon size={40} className="text-brand-lime" />
                  </div>
                  <h3 className="font-oswald text-2xl uppercase text-white mb-3 group-hover:text-brand-lime transition-colors">
                    {tile.title}
                  </h3>
                  <p className="font-montserrat text-brand-gray mb-6">
                    {tile.description}
                  </p>
                  <div className="inline-flex items-center gap-2 font-oswald uppercase tracking-wider text-brand-lime">
                    <span>{tile.cta}</span>
                    <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section data-testid="watts-merch-section" className="py-20 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <Zap size={32} className="text-yellow-400" />
            </div>
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-lime mb-4">
              Feel The Power
            </p>
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-4">
              2000WATTS Merch
            </h2>
            <p className="font-montserrat text-brand-gray max-w-xl mx-auto">
              Wear the energy. Rep the grind. Official 2000WATTS gear for those who show up and put in the work.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {merchandise.map((item, index) => (
              <Card 
                key={item.name}
                testId={`watts-merch-${index}`}
                className="p-8 text-center group hover:border-brand-lime border-2 border-brand-lime/20 transition-all bg-brand-dark"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-lime/10 flex items-center justify-center group-hover:bg-brand-lime/20 transition-colors relative">
                  <Zap size={36} className="text-brand-lime" />
                  <div className="absolute inset-0 rounded-full border-2 border-brand-lime/30 group-hover:border-brand-lime/60 transition-colors" />
                </div>
                <h3 className="font-oswald text-lg uppercase text-white mb-2">{item.name}</h3>
                <p className="font-oswald text-2xl text-brand-lime mb-6">{item.price}</p>
                <SkewButton 
                  href="https://viagro.com" 
                  testId={`shop-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="w-full justify-center"
                >
                  {item.cta}
                </SkewButton>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About 2000WATTS Section */}
      <section id="about-2000watts" data-testid="watts-about-section" className="py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="bg-brand-black border-2 border-brand-lime/30 p-8 md:p-16 relative overflow-hidden">
              {/* Electric accents */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-brand-lime/5 rounded-full -translate-y-1/2 -translate-x-1/2" />
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-brand-lime/5 rounded-full translate-y-1/2 translate-x-1/2" />
              
              <div className="relative z-10 text-center">
                <img 
                  src={WATTS_LOGO} 
                  alt="2000WATTS" 
                  className="h-28 mx-auto mb-8"
                />
                
                <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter text-white mb-6">
                  About <span className="text-brand-lime">2000WATTS</span>
                </h2>
                
                <div className="w-32 h-1 bg-brand-lime mx-auto mb-8" />
                
                <p className="font-montserrat text-lg text-brand-gray leading-relaxed mb-6">
                  2000WATTS is the energy behind the movement. It's not just a brand — it's a mindset. 
                  <span className="text-white font-semibold"> Strong. Powerful. No excuses.</span>
                </p>
                
                <p className="font-montserrat text-lg text-brand-gray leading-relaxed mb-8">
                  Born from the belief that every woman has untapped power within her, 2000WATTS represents the intensity, 
                  the drive, and the relentless pursuit of becoming your strongest self. When you wear the lightning, 
                  you carry that energy with you.
                </p>
                
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
                    <span className="font-oswald text-brand-lime uppercase tracking-wider">Energy</span>
                  </div>
                  <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
                    <span className="font-oswald text-brand-lime uppercase tracking-wider">Intensity</span>
                  </div>
                  <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
                    <span className="font-oswald text-brand-lime uppercase tracking-wider">Power</span>
                  </div>
                  <div className="px-6 py-3 bg-brand-lime/10 border border-brand-lime/30">
                    <span className="font-oswald text-brand-lime uppercase tracking-wider">Results</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="watts-cta" className="py-24 bg-brand-black relative overflow-hidden">
        {/* Electric background effect */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-brand-lime/10 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <div className="flex justify-center mb-6">
            <Zap size={48} className="text-yellow-400" />
          </div>
          
          <h2 className="font-oswald text-5xl md:text-7xl font-bold uppercase text-white mb-4">
            FEEL THE <span className="text-brand-lime">POWER</span>
          </h2>
          
          <p className="font-oswald text-xl uppercase tracking-[0.2em] text-brand-lime mb-8">
            2000WATTS
          </p>
          
          <p className="font-montserrat text-xl text-brand-gray mb-10">
            No excuses. Just results. Are you ready to shock the body?
          </p>
          
          <SkewButton href="https://viagro.com" testId="watts-final-cta" className="text-lg px-12 py-4">
            Get Started
          </SkewButton>
        </div>
      </section>

      {/* Footer Link back to main site */}
      <div className="bg-brand-dark py-6 text-center border-t border-brand-lime/20">
        <Link to="/" className="font-oswald text-sm uppercase tracking-widest text-brand-gray hover:text-brand-lime transition-colors">
          ← Return to Power Up Your Fitness
        </Link>
      </div>
    </div>
  );
};
